import { DateInput } from '../components/DateTimeInputs';
import { activeLang, useT } from '../lib/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  checkDay,
  displayName,
  driverOf,
  FLAGS,
  lockReason,
  NEW_VEHICLE_PREFIX,
  loadOf,
  missingInfo,
  MISSING_LABELS,
  transferTime,
  tripsOf,
  newBlocks,
  seatsOf,
  segmentsOf,
  SEGMENT_LABELS,
  type AutoPlanResult,
  type BdrStatus,
  type BookingDto,
  type BookingStatus,
  type DayTourDto,
  type DayVehicleDto,
  type TripDriverDto,
  type Departure,
  type PlanChangeEvent,
  type PlanInput,
  type PlanIssue,
  type PlanTour,
  type RefItem,
  type Segment,
} from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useRefData } from '../lib/reference';
import { useAuth } from '../lib/auth';
import { longDate, todayIso } from '../lib/dates';
import { BookingPanel, mayLeaveBookingPanel } from '../components/BookingPanel';
import { StatusChip } from '../components/StatusChip';
import { LangBadge } from '../components/LangBadge';
import { TransferBadge } from '../components/TransferBadge';
import { TransferTime } from '../components/TransferTime';
import { Link } from 'react-router';
import { periodTitle, stepPeriod, TransfersRange, type TransferView } from './TransfersRange';
import { useIsPhone } from '../lib/media';
import { tourTint } from '../lib/tourColor';

const TRAY = 'tray';
const VIEW_LABEL = { day: 'Day', week: 'Week', month: 'Month' } as const;
/** The transfers box under the tray: dropping there also takes a transfer out of its vehicle. */
const TRAY_T = 'tray-transfers';
const str = (v: unknown) => (v === null || v === undefined ? '' : String(v));

interface Day {
  dayVehicles: DayVehicleDto[];
  bookings: BookingDto[];
  dayTours: DayTourDto[];
  tripDrivers?: TripDriverDto[];
}

/** One step the office can undo: the moves it made and the vehicles it opened. */
interface UndoStep {
  label: string;
  moves: { bookingId: string; from: string | null; to: string | null }[];
  created: string[];
}

/** Yellow triangle with a black "!" (warnings) or dark red with a white "!" (something blocks). */
function AlertIcon({ level }: { level: 'block' | 'warning' }) {
  const block = level === 'block';
  return (
    <svg width="18" height="16" viewBox="0 0 18 16" aria-hidden="true">
      <path d="M9 1 L17 15 H1 Z" fill={block ? 'var(--alarm)' : '#f5b400'} stroke={block ? 'var(--alarm)' : '#c98f00'} strokeWidth="1" strokeLinejoin="round" />
      <rect x="8.1" y="5.2" width="1.8" height="5.2" rx=".6" fill={block ? '#fff' : '#111'} />
      <circle cx="9" cy="12.4" r="1" fill={block ? '#fff' : '#111'} />
    </svg>
  );
}

/** Problems that the vehicle header already shows (an empty Van or Guide field outlined in amber). */
const SHOWN_IN_FIELDS = new Set(['no_vehicle', 'no_guide']);

/** Reference lists in the shape the rules engine reads. */
function useRuleLists() {
  const { lists } = useRefData();
  return useMemo(() => {
    if (!lists) return null;
    const tours: PlanTour[] = lists.tours.map((t) => ({
      id: t.id, code: str(t.code), kind: str(t.kind), shift: str(t.shift), minPax: Number(t.minPax ?? 1),
      dedicatedVehicle: !!t.dedicatedVehicle, directionMorning: (t.directionMorning as string) ?? null,
      directionAfternoon: (t.directionAfternoon as string) ?? null, family: (t.family as string) ?? null,
    }));
    return {
      tours,
      tourMap: new Map(tours.map((t) => [t.id, t])),
      vehicles: lists.vehicles.map((v) => ({ id: v.id, plate: str(v.plate), seats: Number(v.seats ?? 8) })),
      guides: lists.guides.map((g) => ({ id: g.id, name: str(g.name), languages: (g.languages as string[]) ?? [] })),
      rules: lists.compatRules.filter((r) => r.active).map((r) => ({
        leftRef: str(r.leftRef), rightRef: str(r.rightRef), allowed: !!r.allowed, priority: Number(r.priority ?? 0),
      })),
    };
  }, [lists]);
}

function toInput(day: Day, l: NonNullable<ReturnType<typeof useRuleLists>>): PlanInput {
  return {
    tours: l.tours, vehicles: l.vehicles, guides: l.guides, rules: l.rules, dayVehicles: day.dayVehicles, dayTours: day.dayTours,
    tripDrivers: day.tripDrivers,
    bookings: day.bookings.map((b) => ({
      id: b.id, reference: b.reference, tourId: b.tourId, pickupTime: b.pickupTime, pax: b.pax, language: b.language,
      status: b.status, dayVehicleId: b.dayVehicleId, missing: missingInfo(b).length > 0, locked: b.planLocked,
      transfer: !!b.transferDirection, transferTime: transferTime(b), pickupPlace: b.pickupPlace ?? b.pickupAddress,
    })),
  };
}

type CardProps = {
  b: BookingDto; tour?: PlanTour; brandColor?: string; tourColor?: string; flash?: string; issue?: 'block' | 'warning'; onOpen?: () => void;
  /** Locked by the booking itself or by its vehicle: cannot be dragged. */
  frozen?: boolean; onLock?: () => void;
  /** Tap on the C / CC / X chip. */
  onStatus?: (next: BookingStatus | null) => void;
};

function CardBody({ b, tour, flash, issue, onStatus }: Pick<CardProps, 'b' | 'tour' | 'flash' | 'issue' | 'onStatus'>) {
  const t = useT();
  const flag = FLAGS.find((f) => f.key === b.flag);
  const missing = missingInfo(b).length > 0;
  return (
    <>
      <span className="mono pcard-time">{b.transferDirection ? <TransferTime b={b} /> : b.pickupTime ?? '--:--'}</span>
      <span className="pcard-name ellipsis" title={b.clientNameLatin ? b.clientName : undefined}>
        {flag && <span className="flag-dot" style={{ background: flag.color }} title={t(flag.label)} />}
        {displayName(b) || b.reference}
      </span>
      <span className="pcard-pax">{b.pax}</span>
      <span className="pcard-sub">
        {b.transferDirection ? <TransferBadge d={b.transferDirection} checked={b.expectedChecked} /> : <b>{tour?.code ?? '?'}</b>}
        {b.transferDirection && b.transportRef && <span className="mono pcard-tr">{b.transportRef}</span>}
        <LangBadge code={b.language} />
        <StatusChip status={b.status} onAdvance={onStatus} />
        {missing && <span className="badge badge-warn">{t('missing')}</span>}
        {flash && <span className="badge badge-accent">{flash}</span>}
        {b.transferDirection && <span className="pcard-route ellipsis" title={`${b.pickupPlace ?? ''} → ${b.dropoffPlace ?? ''}`}>{b.pickupPlace || '?'} → {b.dropoffPlace || '?'}</span>}
        {issue === 'block' && <span className="pcard-bad" title={t('This vehicle breaks a rule with this booking: see the list below the vehicle.')}>!</span>}
      </span>
    </>
  );
}

function BookingCard({ b, tour, brandColor, tourColor, flash, issue, onOpen, frozen, onLock, onStatus }: CardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: b.id, disabled: frozen });
  const t = useT();
  const cls = ['pcard', tourColor ? 'tinted' : '', isDragging ? 'dragging' : '', flash ? 'flash' : '', issue ?? '', frozen ? 'frozen' : ''].filter(Boolean).join(' ');
  return (
    <div ref={setNodeRef} className={cls} style={{ ...tourTint(tourColor), borderLeftColor: brandColor }} {...listeners} {...attributes}
      onClick={onOpen} onKeyDown={(e) => { if (e.key === 'Enter' && onOpen) onOpen(); else listeners?.onKeyDown?.(e); }}
      aria-label={t('{ref}, {pax} pax, {tour}. Enter to open. Space to pick up, arrows to move, space to drop.', { ref: b.reference, pax: b.pax, tour: tour?.code ?? t('no tour') })}>
      <CardBody b={b} tour={tour} flash={flash} issue={issue} onStatus={onStatus} />
      {onLock && (
        <button type="button" className={`pcard-lock ${b.planLocked ? 'on' : ''}`} title={b.planLocked ? t('Unlock') : t('Lock in this vehicle')}
          aria-label={b.planLocked ? t('Unlock {ref}', { ref: b.reference }) : t('Lock {ref} in this vehicle', { ref: b.reference })} aria-pressed={b.planLocked}
          onPointerDown={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onLock(); }}>
          <LockIcon on={b.planLocked} />
        </button>
      )}
    </div>
  );
}

function LockIcon({ on }: { on: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d={on ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 7.5-2'} />
    </svg>
  );
}

const DEPARTURE_LABEL: Record<Departure, string> = { guaranteed: 'Guaranteed', cancelled: 'Cancelled today' };

/** A tour's settings for this date only: minimum pax, guaranteed or cancelled departure. */
function TourDaySheet({ tour, pax, current, date, onClose, onSaved }: {
  tour: PlanTour; pax: number; current?: DayTourDto; date: string; onClose: () => void; onSaved: () => void;
}) {
  const [minPax, setMinPax] = useState(current?.minPax === null || current?.minPax === undefined ? '' : String(current.minPax));
  const [departure, setDeparture] = useState<Departure | ''>(current?.departure ?? '');
  const [error, setError] = useState('');
  const t = useT();
  async function save(e: React.FormEvent) {
    e.preventDefault();
    const min = minPax.trim() === '' ? null : Number(minPax);
    if (min !== null && !(Number.isInteger(min) && min >= 1 && min <= 60)) return setError(t('Minimum: a number from 1 to 60, or empty.'));
    try {
      await api('/api/plan/tours', { method: 'PUT', body: { date, tourId: tour.id, minPax: min, departure: departure || null, version: current?.version ?? null } });
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    }
  }
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet card" onClick={(e) => e.stopPropagation()} onSubmit={save} aria-label={t('{tour} today', { tour: tour.code })}>
        <div className="sheet-head"><h2>{t('{tour} on {date}', { tour: tour.code, date: longDate(date) })}</h2><button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{t('Close')}</button></div>
        <div className="sheet-body">
          <p className="muted">{t('{n} pax booked today. These settings are for this date only.', { n: pax })}</p>
          <label className="field">
            <span>{t('Minimum pax today')}</span>
            <input className="input" type="number" min={1} max={60} value={minPax} placeholder={t('Usual: {n}', { n: tour.minPax })} onChange={(e) => setMinPax(e.target.value)} />
          </label>
          <fieldset className="field">
            <legend>{t('Departure')}</legend>
            <div className="seg">
              <button type="button" aria-pressed={departure === ''} onClick={() => setDeparture('')}>{t('Normal')}</button>
              <button type="button" aria-pressed={departure === 'guaranteed'} onClick={() => setDeparture('guaranteed')}>{t('Guaranteed')}</button>
              <button type="button" aria-pressed={departure === 'cancelled'} onClick={() => setDeparture('cancelled')}>{t('Cancelled today')}</button>
            </div>
          </fieldset>
          {departure === 'cancelled' && <div className="alert alert-warn">{t('Its bookings stay as they are: move each one to another tour or cancel it.')}</div>}
          {error && <div className="alert alert-bad">{error}</div>}
          {current?.updatedBy && <div className="muted">{t('Last changed by {who}.', { who: current.updatedBy })}</div>}
        </div>
        <div className="sheet-foot"><button className="btn btn-primary">{t('Save')}</button></div>
      </form>
    </div>
  );
}

/** Seats for this date: saved when leaving the field or pressing Enter, not at every key. */
function SeatsInput({ value, fallback, label, onSave }: { value: number | null; fallback: number; label: string; onSave: (v: number | null) => void }) {
  const [text, setText] = useState(value === null ? '' : String(value));
  useEffect(() => setText(value === null ? '' : String(value)), [value]);
  const commit = () => {
    const v = text.trim() === '' ? null : Number(text);
    if (v !== null && !(Number.isInteger(v) && v >= 1 && v <= 60)) return setText(value === null ? '' : String(value));
    if (v !== value) onSave(v);
  };
  return (
    <input className="input" type="number" min={1} max={60} value={text} placeholder={String(fallback)} aria-label={label}
      onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
  );
}

function Drop({ id, domId, className, children }: { id: string; domId?: string; className: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return <div ref={setNodeRef} id={domId} className={`${className} ${isOver ? 'over' : ''}`}>{children}</div>;
}

/** Scroll the phone board to one vehicle (or the tray), below the sticky bars. */
function jumpTo(domId: string) {
  document.getElementById(domId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/**
 * One board, two pages (Bruno, 18/10): "Planning" shows only the tour vehicles and the tours to place;
 * "Transfers" shows only the transfer vehicles and the transfers to place, so each team works without
 * the other's cards. The rules are checked on the whole day either way.
 */
export function Planning({ mode = 'tours' }: { mode?: 'tours' | 'transfers' }) {
  const isTr = mode === 'transfers';
  const kind = isTr ? 'transfers' : 'tours';
  const [params, setParams] = useSearchParams();
  const date = params.get('date') ?? todayIso();
  const { lists, byId } = useRefData();
  const rl = useRuleLists();
  const { me } = useAuth();
  const myId = me?.user.id;
  const [day, setDay] = useState<Day | null>(null);
  const [error, setError] = useState('');
  const [notice, setNoticeText] = useState('');
  // Refusals and errors are important (dark red); a finished action is good news (green).
  const [noticeGood, setNoticeGood] = useState(false);
  const setNotice = (text: string, good = false) => { setNoticeText(text); setNoticeGood(good); };
  const [online, setOnline] = useState(true);
  const [dragging, setDragging] = useState<BookingDto | null>(null);
  const [open, setOpenRaw] = useState<BookingDto | null>(null);
  /** Opening another booking (or a new one) asks first when the panel holds unsaved changes. */
  const setOpen = (v: BookingDto | null) => { if (v !== open && mayLeaveBookingPanel()) setOpenRaw(v); };
  const [flash, setFlash] = useState<Record<string, string>>({});
  const [undo, setUndo] = useState<UndoStep[]>([]);
  const [proposal, setProposal] = useState<AutoPlanResult | null>(null);
  const [tourSheet, setTourSheet] = useState<string | null>(null);
  /** Which vehicle's alert list is open (small pop-up next to its icon), and the full list of the day. */
  const [alertsOf, setAlertsOf] = useState<string | null>(null);
  const [allAlerts, setAllAlerts] = useState(false);
  const [working, setWorking] = useState(false);
  const [pmOpen, setPmOpen] = useState<Set<string>>(new Set());
  // Phone: no dragging (scrolling and dragging fight each other); a tap opens a "Move to" sheet instead.
  const phone = useIsPhone();
  const [moving, setMoving] = useState<BookingDto | null>(null);
  const t = useT();
  const busy = useRef(false);

  // Undo belongs to one day of one screen.
  useEffect(() => setUndo([]), [date]);
  const pushUndo = (step: UndoStep) => setUndo((u) => [step, ...u].slice(0, 20));

  const [bdr, setBdr] = useState<Record<string, BdrStatus>>({});

  const load = useCallback(async () => {
    try {
      const [r, s] = await Promise.all([
        api<Day>(`/api/plan?date=${date}`),
        api<{ status: Record<string, BdrStatus> }>(`/api/plan/bdr/status?date=${date}`).catch(() => ({ status: {} })),
      ]);
      setDay(r);
      setBdr(s.status);
      setError('');
    } catch (e) {
      setError((e as Error).message);
    }
  }, [date]);

  // First load, then live changes from the other screens.
  useEffect(() => {
    setDay(null);
    void load();
    let timer: ReturnType<typeof setTimeout> | undefined;
    const es = new EventSource(`/api/plan/stream?date=${date}`);
    es.onopen = () => {
      setOnline((was) => {
        if (!was) void load();
        return true;
      });
    };
    es.onerror = () => setOnline(false);
    es.onmessage = (m) => {
      const e = JSON.parse(m.data) as PlanChangeEvent;
      if (e.bookingIds?.length && e.userId !== myId) {
        setFlash((f) => ({ ...f, ...Object.fromEntries(e.bookingIds!.map((id) => [id, e.by])) }));
        setTimeout(() => setFlash((f) => {
          const n = { ...f };
          for (const id of e.bookingIds!) delete n[id];
          return n;
        }), 2500);
      }
      clearTimeout(timer);
      timer = setTimeout(() => { if (!busy.current) void load(); }, 150);
    };
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, [date, load, myId]);

  const pointer = useSensor(PointerSensor, { activationConstraint: { distance: 6 } });
  const touch = useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } });
  const keyboard = useSensor(KeyboardSensor);
  const desktopSensors = useSensors(pointer, touch, keyboard);
  const phoneSensors = useSensors(keyboard);
  const sensors = phone ? phoneSensors : desktopSensors;

  const issues: PlanIssue[] = useMemo(() => (day && rl ? checkDay(toInput(day, rl), t) : []), [day, rl, t]);
  /**
   * What the alert icons and the "alerts" count show: the same issues, except that "n bookings miss
   * information" becomes one line per booking saying what is missing (Bruno, 18/10: 3 bookings = 3 alerts).
   */
  const alerts: PlanIssue[] = useMemo(() => issues.flatMap((i) => {
    if (i.code !== 'missing_info' || !i.bookingIds?.length || !day) return [i];
    return i.bookingIds.map((id) => {
      const b = day.bookings.find((x) => x.id === id);
      if (!b) return { ...i, bookingIds: [id] };
      const what = missingInfo(b).map((m) => t(MISSING_LABELS[m])).join(', ');
      return { ...i, bookingIds: [id], message: `${b.pickupTime ? `${b.pickupTime} ` : ''}${displayName(b) || b.reference}: ${what}` };
    });
  }), [issues, day, t]);

  const live = (day?.bookings ?? []).filter((b) => b.status !== 'X');
  const isHere = (b: BookingDto) => !!b.transferDirection === isTr;
  const liveHere = live.filter(isHere);
  const vehiclesHere = (day?.dayVehicles ?? []).filter((d) => (d.kind === 'transfers') === isTr);
  // Planning also shows, last and in grey, the transfer vehicles that already carry transfers (Bruno, 18/10),
  // so the day's whole fleet is in view. They are worked on in the Transfers page.
  const boardVehicles = isTr ? vehiclesHere : [
    ...vehiclesHere,
    ...(day?.dayVehicles ?? []).filter((d) => d.kind === 'transfers' && live.some((b) => b.dayVehicleId === d.id)),
  ];
  const tray = live.filter((b) => !b.dayVehicleId && !b.transferDirection);
  const transfersToday = live.filter((b) => !!b.transferDirection).sort((a, c) => (transferTime(a) ?? '99').localeCompare(transferTime(c) ?? '99'));
  const transfersToPlace = transfersToday.filter((b) => !b.dayVehicleId);
  const cancelledToday = (day?.bookings ?? []).filter((b) => b.status === 'X' && isHere(b));
  const toPlace = isTr ? transfersToPlace : tray;
  const brandColor = (b: BookingDto) => str(byId('brands', b.brandId)?.color) || undefined;
  const tourOf = (b: BookingDto) => (b.tourId ? rl?.tourMap.get(b.tourId) : undefined);
  const colorOf = (tourId: string | null | undefined) => str(byId('tours', tourId ?? null)?.color) || undefined;

  const bookingIssue = (id: string): 'block' | 'warning' | undefined => {
    const mine = issues.filter((i) => i.bookingIds?.includes(id));
    if (mine.some((i) => i.level === 'block')) return 'block';
    if (mine.some((i) => i.level === 'warning' && i.code !== 'below_minimum')) return 'warning';
    return undefined;
  };

  async function move(b: BookingDto, to: string | null) {
    if (!day || !rl || b.dayVehicleId === to) return;
    if (!online) return setNotice(t('No connection: moves are paused until it returns.'));
    const locked = lockReason(toInput(day, rl), b.id, to, t);
    if (locked) return setNotice(locked);
    const next = { ...day, bookings: day.bookings.map((x) => (x.id === b.id ? { ...x, dayVehicleId: to } : x)) };
    const blocks = newBlocks(checkDay(toInput(day, rl), t), checkDay(toInput(next, rl), t));
    if (blocks.length) return setNotice(blocks.map((i) => i.message).join(' '));
    setNotice('');
    setDay(next);
    busy.current = true;
    try {
      await api('/api/plan/assign', { method: 'POST', body: { bookingId: b.id, from: b.dayVehicleId, to } });
      pushUndo({ label: t('move {ref}', { ref: b.reference }), moves: [{ bookingId: b.id, from: b.dayVehicleId, to }], created: [] });
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : t('The move was not saved.'));
    } finally {
      busy.current = false;
      void load();
    }
  }

  function onDragStart(e: DragStartEvent) {
    setDragging(day?.bookings.find((b) => b.id === e.active.id) ?? null);
  }
  function onDragEnd(e: DragEndEvent) {
    setDragging(null);
    const b = day?.bookings.find((x) => x.id === e.active.id);
    if (!b || !e.over) return;
    void move(b, e.over.id === TRAY || e.over.id === TRAY_T ? null : String(e.over.id));
  }

  /** Phone "Move to": open a new vehicle and put the booking in it, as one undo step. */
  async function moveToNew(b: BookingDto) {
    if (!online) return setNotice(t('No connection: moves are paused until it returns.'));
    setWorking(true);
    busy.current = true;
    let created: string | null = null;
    try {
      const r = await api<{ dayVehicle: DayVehicleDto }>('/api/plan/vehicles', { method: 'POST', body: { date, kind } });
      created = r.dayVehicle.id;
      await api('/api/plan/assign', { method: 'POST', body: { bookingId: b.id, from: b.dayVehicleId, to: created } });
      pushUndo({ label: t('move {ref} to new V{n}', { ref: b.reference, n: r.dayVehicle.number }), moves: [{ bookingId: b.id, from: b.dayVehicleId, to: created }], created: [created] });
      setNotice('');
      setTimeout(() => jumpTo(`plan-v-${created}`), 300);
    } catch (e) {
      // The booking did not go in: do not leave an empty vehicle behind.
      if (created) await api(`/api/plan/vehicles/${created}`, { method: 'DELETE' }).catch(() => undefined);
      setNotice(e instanceof ApiError ? e.message : t('The move was not saved.'));
    } finally {
      busy.current = false;
      setWorking(false);
      void load();
    }
  }

  /** C → CC → X → empty from the card. X takes the booking out of its vehicle (the database does it). */
  async function setStatus(b: BookingDto, status: BookingStatus | null) {
    if (!online) return setNotice(t('No connection: moves are paused until it returns.'));
    setDay((d) => (d ? { ...d, bookings: d.bookings.map((x) => (x.id === b.id ? { ...x, status } : x)) } : d));
    busy.current = true;
    try {
      await api(`/api/bookings/${b.id}`, { method: 'PATCH', body: { version: b.version, status } });
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : t('Could not change the status.'));
    } finally {
      busy.current = false;
      void load();
    }
  }

  async function addVehicle(kind: 'tours' | 'transfers' = 'tours') {
    try {
      await api('/api/plan/vehicles', { method: 'POST', body: { date, kind } });
      await load();
    } catch (e) {
      setNotice((e as Error).message);
    }
  }

  async function patchVehicle(dv: DayVehicleDto, values: Partial<Pick<DayVehicleDto, 'vehicleId' | 'guideId' | 'pmGuideId' | 'seats' | 'locked'>>) {
    if (!day || !rl) return;
    const next = { ...day, dayVehicles: day.dayVehicles.map((d) => (d.id === dv.id ? { ...d, ...values } : d)) };
    const blocks = newBlocks(checkDay(toInput(day, rl), t), checkDay(toInput(next, rl), t));
    if (blocks.length) return setNotice(blocks.map((i) => i.message).join(' '));
    setNotice('');
    setDay(next);
    try {
      await api(`/api/plan/vehicles/${dv.id}`, { method: 'PATCH', body: { version: dv.version, ...values } });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      void load();
    }
  }

  /**
   * One trip of a transfer vehicle: another driver (null gives it back to the vehicle's driver) and the
   * OBSERVATIONS printed on its BDR. A value left out is unchanged.
   */
  async function setTrip(dv: DayVehicleDto, time: string | null, values: { guideId?: string | null; notes?: string | null }) {
    if (!day) return;
    const key = time ?? '';
    const cur = day.tripDrivers?.find((x) => x.dayVehicleId === dv.id && x.time === key);
    const guideId = values.guideId === undefined ? cur?.guideId ?? null : values.guideId && values.guideId !== dv.guideId ? values.guideId : null;
    const notes = values.notes === undefined ? cur?.notes ?? null : values.notes?.trim() || null;
    if (guideId === (cur?.guideId ?? null) && notes === (cur?.notes ?? null)) return;
    const rest = (day.tripDrivers ?? []).filter((x) => x !== cur);
    setNotice('');
    setDay({ ...day, tripDrivers: guideId || notes ? [...rest, { dayVehicleId: dv.id, time: key, guideId, notes, version: cur?.version ?? 1, updatedBy: null }] : rest });
    try {
      await api('/api/plan/trips', { method: 'PUT', body: { dayVehicleId: dv.id, time: key, ...values, version: cur?.version ?? null } });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      void load();
    }
  }

  async function removeVehicle(dv: DayVehicleDto) {
    try {
      await api(`/api/plan/vehicles/${dv.id}`, { method: 'DELETE' });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      void load();
    }
  }

  async function toggleLock(b: BookingDto) {
    try {
      await api('/api/plan/lock', { method: 'POST', body: { bookingId: b.id, locked: !b.planLocked } });
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      void load();
    }
  }

  async function runUndo() {
    const step = undo[0];
    if (!step || !online) return;
    setWorking(true);
    const problems: string[] = [];
    for (const m of [...step.moves].reverse()) {
      try {
        await api('/api/plan/assign', { method: 'POST', body: { bookingId: m.bookingId, from: m.to, to: m.from } });
      } catch (e) {
        problems.push((e as Error).message);
      }
    }
    for (const id of step.created) {
      try {
        await api(`/api/plan/vehicles/${id}`, { method: 'DELETE' });
      } catch {
        /* a vehicle someone used meanwhile stays */
      }
    }
    setUndo((u) => u.slice(1));
    setNotice(problems.length ? t('Undo done in part: {list}', { list: problems.join(' ') }) : t('Undone: {what}.', { what: step.label }), !problems.length);
    setWorking(false);
    void load();
  }

  /** Takes every placed booking out of its vehicle (except what is locked), as one undo step. */
  async function clearDay() {
    if (!day) return;
    const lockedV = new Set(day.dayVehicles.filter((d) => d.locked || (d.kind === 'transfers') !== isTr).map((d) => d.id));
    const n = day.bookings.filter((b) => b.dayVehicleId && !b.planLocked && !lockedV.has(b.dayVehicleId)).length;
    if (!n) return;
    const ok = window.confirm(t('Take the {n} placed bookings out of their vehicles? Locked bookings and locked vehicles stay. Vehicles keep their van and guide. BDRs already sent will be out of date. You can undo.', { n }));
    if (!ok) return;
    setWorking(true);
    try {
      const r = await api<{ cleared: { bookingId: string; from: string }[]; kept: number }>('/api/plan/clear', { method: 'POST', body: { date, kind } });
      pushUndo({ label: t('clear the day'), moves: r.cleared.map((c) => ({ bookingId: c.bookingId, from: c.from, to: null })), created: [] });
      setNotice(t('{n} bookings back in To place.', { n: r.cleared.length }), true);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setWorking(false);
      void load();
    }
  }

  async function proposeAuto() {
    setWorking(true);
    try {
      setProposal(await api<AutoPlanResult>('/api/plan/auto', { method: 'POST', body: { date } }));
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setWorking(false);
    }
  }

  async function applyAuto() {
    if (!proposal) return;
    setWorking(true);
    try {
      const r = await api<{ applied: { bookingId: string; to: string }[]; skipped: { reason: string }[]; createdVehicles: string[] }>(
        '/api/plan/auto/apply', { method: 'POST', body: { date, newVehicles: proposal.newVehicles, moves: proposal.moves } },
      );
      pushUndo({ label: t('auto-plan'), moves: r.applied.map((a) => ({ bookingId: a.bookingId, from: null, to: a.to })), created: r.createdVehicles });
      setNotice(`${r.applied.length === 1 ? t('1 booking placed.') : t('{n} bookings placed.', { n: r.applied.length })}${r.skipped.length ? ` ${t('{n} skipped:', { n: r.skipped.length })} ${r.skipped.map((x) => x.reason).join(' ')}` : ''}`, !r.skipped.length);
      setProposal(null);
    } catch (e) {
      setNotice((e as Error).message);
    } finally {
      setWorking(false);
      void load();
    }
  }

  /**
   * Opens the BDR. On a phone it opens the share sheet (WhatsApp) with the PDF attached when the
   * browser can; otherwise the PDF opens in a new tab, ready to print.
   */
  async function openBdr(dv: DayVehicleDto | null, doc: 'bdr' | 'signs' | 'sheet' = 'bdr') {
    // A transfer vehicle has its own BDR, also when its button is shown on the tours planning.
    const transfers = dv ? dv.kind === 'transfers' : isTr;
    const base = doc === 'sheet' ? '/api/plan/sheet' : doc === 'signs' ? '/api/plan/signs' : transfers ? '/api/plan/transfer-bdr' : '/api/plan/bdr';
    const url = `${base}?date=${date}${dv ? `&v=${dv.id}` : ''}${doc === 'sheet' ? `&lang=${activeLang}` : ''}`;
    const name = `${doc === 'sheet' ? 'Planning' : doc === 'signs' ? 'Welcome' : transfers ? 'BDR transfer' : 'BDR'} ${date}${dv ? ` V${dv.number}` : ''}.pdf`;
    const phone = window.matchMedia('(pointer: coarse)').matches && typeof navigator.canShare === 'function';
    if (!phone) {
      window.open(url, '_blank', 'noopener');
      setTimeout(() => void load(), 1500);
      return;
    }
    try {
      const res = await fetch(url, { credentials: 'same-origin', headers: { 'x-lang': activeLang } });
      if (!res.ok) throw new Error(t('The BDR could not be made.'));
      const file = new File([await res.blob()], name, { type: 'application/pdf' });
      if (navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: name });
      else window.open(URL.createObjectURL(file), '_blank');
    } catch (e) {
      if ((e as Error).name !== 'AbortError') setNotice((e as Error).message);
    } finally {
      void load();
    }
  }

  const view: TransferView = isTr ? ((['week', 'month'].includes(params.get('view') ?? '') ? params.get('view') : 'day') as TransferView) : 'day';
  const goTo = (d: string, v: TransferView = view) => setParams({ ...(d === todayIso() ? {} : { date: d }), ...(v === 'day' ? {} : { view: v }) });

  const datebar = (
      <div className="datebar">
        <button className="btn btn-sm" onClick={() => goTo(stepPeriod(view, date, -1))} aria-label={t('Previous')}>‹</button>
        {view === 'day' && <DateInput value={date} onChange={(d) => goTo(d)} required aria-label={t('Date')} />}
        <button className="btn btn-sm" onClick={() => goTo(stepPeriod(view, date, 1))} aria-label={t('Next')}>›</button>
        {date !== todayIso() && <button className="btn btn-sm" onClick={() => goTo(todayIso())}>{t('Today')}</button>}
        <h2 className="datebar-title">{view === 'day' ? longDate(date) : periodTitle(view, date, t)}</h2>
        {isTr && (
          <div className="seg datebar-seg" role="group" aria-label={t('View')}>
            {(['day', 'week', 'month'] as const).map((v) => (
              <button key={v} type="button" aria-pressed={view === v} onClick={() => goTo(date, v)}>{t(VIEW_LABEL[v])}</button>
            ))}
          </div>
        )}
      </div>
  );

  const trayGroups = useMemo(() => {
    const order = new Map((lists?.tours ?? []).map((t, i) => [t.id, i] as const));
    const m = new Map<string, BookingDto[]>();
    for (const b of tray) m.set(b.tourId ?? 'none', [...(m.get(b.tourId ?? 'none') ?? []), b]);
    return [...m.entries()].sort(([a], [c]) => (order.get(a) ?? 9999) - (order.get(c) ?? 9999));
  }, [tray, lists]);

  const toursToday = useMemo(() => {
    if (!rl) return [];
    const pax = new Map<string, number>();
    for (const b of live) if (b.tourId && !b.transferDirection) pax.set(b.tourId, (pax.get(b.tourId) ?? 0) + b.pax);
    const order = new Map((lists?.tours ?? []).map((t, i) => [t.id, i] as const));
    return [...pax.entries()]
      .map(([id, p]) => ({ tour: rl.tourMap.get(id)!, pax: p, setting: day?.dayTours.find((d) => d.tourId === id) }))
      .filter((x) => x.tour)
      .sort((a, b) => (order.get(a.tour.id) ?? 0) - (order.get(b.tour.id) ?? 0));
  }, [live, rl, day, lists]);

  const clearable = !!day && day.bookings.some((b) => b.dayVehicleId && !b.planLocked && vehiclesHere.some((d) => d.id === b.dayVehicleId && !d.locked));
  // A van may carry tours and transfers on the same day (Bruno, 19/10/2026): only the same kind of vehicle counts.
  // Transfer drivers can change from one trip to another, so the board only warns about them.
  const sameKind = (day?.dayVehicles ?? []).filter((d) => (d.kind ?? 'tours') === (isTr ? 'transfers' : 'tours'));
  const usedVehicles = new Set(sameKind.map((d) => d.vehicleId).filter(Boolean));
  const usedGuides = new Set(isTr ? [] : sameKind.map((d) => d.guideId).filter(Boolean));
  // An alert belongs to this page when it is about one of its vehicles or one of its bookings.
  const hereIds = new Set(liveHere.map((b) => b.id));
  const vHere = new Set(vehiclesHere.map((d) => d.id));
  const belongs = (i: PlanIssue) => (i.dayVehicleId ? vHere.has(i.dayVehicleId) : i.bookingIds?.length ? i.bookingIds.some((id) => hereIds.has(id)) : !isTr);
  const alertsHere = alerts.filter(belongs);
  // Tours below their minimum already show in amber in the tour chips.
  const dayIssues = issues.filter(belongs).filter((i) => !i.dayVehicleId && i.level !== 'info' && i.code !== 'below_minimum');
  // Same count as the icons: an empty van or guide field is already shown by its dark red border.
  const blockCount = alertsHere.filter((i) => i.level === 'block' && !SHOWN_IN_FIELDS.has(i.code)).length;
  const warnCount = alertsHere.filter((i) => i.level === 'warning' && !SHOWN_IN_FIELDS.has(i.code)).length;

  if (view !== 'day') {
    return (
      <div className="stack plan-page">
        <div className="page-head" style={{ marginBottom: 0 }}><h1>{t('Transfers')}</h1></div>
        {datebar}
        <TransfersRange view={view} date={date} onDay={(d) => goTo(d, 'day')} onOpen={(b) => setOpen(b)} />
        {open && <BookingPanel key={open.id} booking={open} date={open.date} onClose={() => setOpenRaw(null)} onSaved={() => setOpenRaw(null)} />}
      </div>
    );
  }

  return (
    <div className="stack plan-page">
      <div className="page-head" style={{ marginBottom: 0 }}>
        <h1>{t(isTr ? 'Transfers' : 'Planning')}</h1>
        <div className="plan-actions">
          <button className="btn" onClick={runUndo} disabled={!undo.length || !online || working} title={undo[0] ? t('Undo {what}', { what: undo[0].label }) : t('Nothing to undo')}>
            {undo[0] ? t('Undo {what}', { what: undo[0].label }) : t('Undo')}
          </button>
          <button className="btn" onClick={clearDay} disabled={!online || working || !clearable} title={t('Take the placed bookings out of their vehicles')}>{t('Clear')}</button>
          {!isTr && <button className="btn" onClick={proposeAuto} disabled={!online || working || !tray.length}>{t('Auto-plan')}</button>}
          <button className="btn" onClick={() => openBdr(null, 'sheet')} disabled={!day?.bookings.length} title={t('Every vehicle of the day on one list, to check and print (A4 landscape)')}>{t('Day sheet')}</button>
          <button className="btn" onClick={() => openBdr(null)} disabled={!vehiclesHere.length}>{t('BDR of the day')}</button>
          {isTr && <button className="btn plan-welcome" onClick={() => openBdr(null, 'signs')} disabled={!vehiclesHere.length} title={t('One A4 sign per arriving client, with the logo of the booking’s brand')}>WELCOME</button>}
          <button className="btn btn-primary" onClick={() => addVehicle(kind)} disabled={!online}>{t(isTr ? 'Add transfer vehicle' : 'Add vehicle')}</button>
        </div>
      </div>

      {datebar}

      <div className="kpis">
        <div className="kpi"><b>{liveHere.length}</b><span>{t(isTr ? 'transfers' : 'bookings')}</span></div>
        <div className="kpi"><b>{liveHere.reduce((s, b) => s + b.pax, 0)}</b><span>{t('pax')}</span></div>
        <div className="kpi"><b>{vehiclesHere.length}</b><span>{t('vehicles')}</span></div>
        <div className={`kpi ${toPlace.length ? 'kpi-warn' : ''}`}><b>{toPlace.length}</b><span>{t('to place')}</span></div>
        <button type="button" className={`kpi kpi-btn ${blockCount ? 'kpi-bad' : warnCount ? 'kpi-warn' : ''}`} onClick={() => setAllAlerts(true)}
          disabled={!blockCount && !warnCount} title={t('See all the alerts of the day')}>
          <b>{blockCount + warnCount}</b><span>{t('alerts')}</span>
        </button>
      </div>

      {!online && <div className="alert alert-bad">{t('Connection lost. Moves are paused until it returns; nothing is lost.')}</div>}
      {notice && <div className={`alert ${noticeGood ? 'alert-good' : 'alert-bad'} alert-row`} role="status"><span>{notice}</span> <button className="btn btn-sm alert-ok" onClick={() => setNotice('')}>{t('OK')}</button></div>}
      {error && <div className="alert alert-bad">{error}</div>}

      {!day || !rl ? <div className="muted">{t('Loading…')}</div> : (
        <>
        {phone && (
          <nav className="plan-jump" aria-label={t('Go to')}>
            <button type="button" className={toPlace.length ? 'warn' : ''} onClick={() => jumpTo(isTr ? 'plan-transfers' : 'plan-tray')}>{t('To place')} {toPlace.length}</button>
            {boardVehicles.map((dv) => {
              const level = issues.some((i) => i.dayVehicleId === dv.id && i.level === 'block') ? 'bad'
                : issues.some((i) => i.dayVehicleId === dv.id && i.level === 'warning') ? 'warn' : '';
              return <button key={dv.id} type="button" className={level} onClick={() => jumpTo(`plan-v-${dv.id}`)}>V{dv.number}</button>;
            })}
          </nav>
        )}
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
          <div className="plan">
            <div className="plan-left">
            {!isTr && (
            <Drop id={TRAY} domId="plan-tray" className="card plan-tray">
              <div className="plan-col-head">
                <b>{t('To place')}</b>
                <span className="muted">{tray.length} · {tray.reduce((s, b) => s + b.pax, 0)} pax</span>
              </div>
              {toursToday.length > 0 && (
                <div className="plan-tours" aria-label={t('Tours today')}>
                  {toursToday.map(({ tour, pax, setting }) => {
                    const min = setting?.minPax ?? tour.minPax;
                    const state = setting?.departure === 'cancelled' ? 'off' : setting?.departure === 'guaranteed' ? 'sure' : tour.kind === 'shared' && pax < min ? 'low' : '';
                    return (
                      <button key={tour.id} type="button" className={`plan-tour ${state} ${!state && colorOf(tour.id) ? 'tinted' : ''}`} style={state ? undefined : tourTint(colorOf(tour.id))} onClick={() => setTourSheet(tour.id)}
                        title={[
                          tour.kind === 'shared' ? t('{pax} pax booked, minimum {min} for the tour to run.', { pax, min }) : t('{pax} pax booked (no minimum).', { pax }),
                          setting?.departure ? t(DEPARTURE_LABEL[setting.departure]) : '',
                          t('Click to change for this day.'),
                        ].filter(Boolean).join(' ')}>
                        <b>{tour.code}</b> {pax}{state === 'low' ? ` · ${t('min {n}', { n: min })}` : ''}
                        {setting?.departure === 'guaranteed' && ' ✓'}
                        {setting?.departure === 'cancelled' && ' ✕'}
                      </button>
                    );
                  })}
                </div>
              )}
              {dayIssues.length > 0 && (
                <ul className="plan-issues">
                  {dayIssues.map((i, k) => <li key={k} className={i.level}>{i.message}</li>)}
                </ul>
              )}
              {trayGroups.length === 0 && <div className="muted plan-empty">{t('Everything is placed.')}</div>}
              {cancelledToday.length > 0 && (
                <details className="plan-group plan-cancelled">
                  <summary className="plan-group-head"><b>{t('Cancelled')}</b><span className="muted">{t('{n} · not counted anywhere', { n: cancelledToday.length })}</span></summary>
                  {cancelledToday.map((b) => (
                    <div key={b.id} className={`pcard ${colorOf(b.tourId) ? 'tinted' : ''}`} style={{ ...tourTint(colorOf(b.tourId)), borderLeftColor: brandColor(b) }}
                      role="button" tabIndex={0} onClick={() => setOpen(b)} onKeyDown={(e) => e.key === 'Enter' && setOpen(b)}>
                      <CardBody b={b} tour={tourOf(b)} onStatus={(s) => void setStatus(b, s)} />
                    </div>
                  ))}
                </details>
              )}
              {trayGroups.map(([tourId, list]) => (
                <div key={tourId} className="plan-group">
                  <div className="plan-group-head">
                    <b>{tourId === 'none' ? t('No tour') : rl.tourMap.get(tourId)?.code}</b>
                    <span className="muted">{list.length} · {list.reduce((s, b) => s + b.pax, 0)} pax</span>
                  </div>
                  {list.map((b) => (
                    <BookingCard key={b.id} b={b} tour={tourOf(b)} brandColor={brandColor(b)} tourColor={colorOf(b.tourId)} flash={flash[b.id]}
                      issue={bookingIssue(b.id)} onOpen={() => (phone ? setMoving(b) : setOpen(b))} onStatus={(s) => void setStatus(b, s)} />
                  ))}
                </div>
              ))}
            </Drop>
            )}
            {!isTr && transfersToday.length > 0 && (
              <section className="card plan-tray plan-transfers plan-tr-summary" aria-label={t('Transfers')}>
                <Link to={`/transfers${date === todayIso() ? '' : `?date=${date}`}`} className="plan-col-head plan-tr-link">
                  <b>{t('Transfers')}</b>
                  <span>{t('{n} to place', { n: transfersToPlace.length })} · {t('{n} placed', { n: transfersToday.length - transfersToPlace.length })}</span>
                </Link>
                <ul className="today-tr-list">
                  {transfersToday.map((b) => {
                    const v = day.dayVehicles.find((x) => x.id === b.dayVehicleId);
                    return (
                      <li key={b.id} className="plan-tr-line">
                        <TransferTime b={b} />
                        <span className="ellipsis">{displayName(b) || b.reference}</span>
                        <span className="muted">{b.pax}</span>
                        <span className={v ? 'today-tr-v' : 'today-tr-v none'}>{v ? `V${v.number}` : t('to place')}</span>
                      </li>
                    );
                  })}
                </ul>
                <Link to={`/transfers${date === todayIso() ? '' : `?date=${date}`}`} className="btn btn-sm">{t('Open Transfers')}</Link>
              </section>
            )}
            {isTr && (
              <Drop id={TRAY_T} domId="plan-transfers" className="card plan-tray plan-transfers">
                <div className="plan-col-head">
                  <b>{t('To place')}</b>
                  <span>{transfersToPlace.length === 1 ? t('1 transfer') : t('{n} transfers', { n: transfersToPlace.length })} · {transfersToPlace.reduce((a, x) => a + x.pax, 0)} {t('pax')}</span>
                </div>
                {dayIssues.length > 0 && (
                  <ul className="plan-issues">
                    {dayIssues.map((i, k) => <li key={k} className={i.level}>{i.message}</li>)}
                  </ul>
                )}
                {transfersToPlace.length === 0 && <div className="muted plan-empty">{t('Every transfer is placed.')}</div>}
                {tripsOf(transfersToPlace.map((b) => ({ ...b, missing: false, transferTime: transferTime(b), transfer: true }))).map((trip) => (
                  <div key={trip.time ?? 'none'} className="plan-group">
                    <div className="plan-group-head">
                      <b className="mono">{trip.time ?? t('no time')}</b>
                      <span className="muted">{trip.bookings.length === 1 ? t('1 transfer') : t('{n} transfers', { n: trip.bookings.length })} · {trip.bookings.reduce((a, x) => a + x.pax, 0)} {t('pax')}</span>
                    </div>
                    {trip.bookings.map((x) => {
                      const b = transfersToPlace.find((y) => y.id === x.id)!;
                      return (
                        <BookingCard key={b.id} b={b} tour={tourOf(b)} brandColor={brandColor(b)} flash={flash[b.id]}
                          issue={bookingIssue(b.id)} onOpen={() => (phone ? setMoving(b) : setOpen(b))} onStatus={(st) => void setStatus(b, st)} />
                      );
                    })}
                  </div>
                ))}
                {cancelledToday.length > 0 && (
                  <details className="plan-group plan-cancelled">
                    <summary className="plan-group-head"><b>{t('Cancelled')}</b><span className="muted">{t('{n} · not counted anywhere', { n: cancelledToday.length })}</span></summary>
                    {cancelledToday.map((b) => (
                      <div key={b.id} className="pcard" style={{ borderLeftColor: brandColor(b) }}
                        role="button" tabIndex={0} onClick={() => setOpen(b)} onKeyDown={(e) => e.key === 'Enter' && setOpen(b)}>
                        <CardBody b={b} tour={tourOf(b)} onStatus={(st) => void setStatus(b, st)} />
                      </div>
                    ))}
                  </details>
                )}
              </Drop>
            )}
            </div>

            <div className="plan-board">
              {vehiclesHere.length === 0 && (
                <div className="card plan-col plan-empty muted">{t(isTr ? 'No transfer vehicle yet. Click “Add transfer vehicle” to start.' : 'No vehicle yet. Click “Add vehicle” to start the day.')}</div>
              )}
              {boardVehicles.map((dv) => {
                const inside = live.filter((b) => b.dayVehicleId === dv.id);
                const seats = seatsOf(dv, rl.vehicles);
                const load = loadOf(inside.map((b) => ({ ...b, missing: false })), rl.tourMap);
                const mine = issues.filter((i) => i.dayVehicleId === dv.id);
                const shown = alerts.filter((i) => i.dayVehicleId === dv.id && i.level !== 'info' && !SHOWN_IN_FIELDS.has(i.code));
                const state = mine.some((i) => i.level === 'block') ? 'block' : mine.some((i) => i.level === 'warning') ? 'warning' : '';
                const bySeg: Record<string, BookingDto[]> = {};
                for (const b of inside) {
                  const segs = segmentsOf(b, tourOf(b));
                  const key = segs.length > 1 ? 'all' : segs[0]!;
                  (bySeg[key] ??= []).push(b);
                }
                const sections: [string, string][] = [['all', 'All day'], ['am', 'Morning'], ['pm', 'Afternoon'], ['eve', 'Evening']];
                const isT = dv.kind === 'transfers';
                const trips = isT ? tripsOf(inside.map((b) => ({ ...b, missing: false, transferTime: transferTime(b), transfer: true }))) : [];
                const vehicle = rl.vehicles.find((v) => v.id === dv.vehicleId);
                return (
                  <Drop key={dv.id} id={dv.id} domId={`plan-v-${dv.id}`} className={`card plan-col ${state} ${isT ? 'plan-col-tr' : ''}`}>
                    <div className="plan-col-head">
                      <b className="plan-nv">V{dv.number}{isT && <span className="plan-nv-tr"> · {t('TRANSFER')}</span>}</b>
                      {shown.length > 0 && (
                        <span className="alert-anchor alert-right">
                          <button type="button" className={`alert-btn ${state}`} aria-expanded={alertsOf === dv.id}
                            aria-label={t('{n} alerts for V{v}', { n: shown.length, v: dv.number })}
                            onClick={() => setAlertsOf((o) => (o === dv.id ? null : dv.id))}>
                            <AlertIcon level={shown.some((i) => i.level === 'block') ? 'block' : 'warning'} /><b>{shown.length}</b>
                          </button>
                          {alertsOf === dv.id && (
                            <>
                              <div className="alert-catch" onClick={() => setAlertsOf(null)} />
                              <div className="alert-pop card" role="dialog" aria-label={t('Alerts for V{n}', { n: dv.number })}>
                                <ul className="plan-issues">{shown.map((i, k) => <li key={k} className={i.level}>{i.message}</li>)}</ul>
                              </div>
                            </>
                          )}
                        </span>
                      )}
                      <button type="button" className={`pcard-lock plan-col-lock ${dv.locked ? 'on' : ''}`} aria-pressed={!!dv.locked}
                        title={dv.locked ? t('Unlock {ref}', { ref: `V${dv.number}` }) : t('Lock V{n}: nothing goes in or out', { n: dv.number })}
                        aria-label={dv.locked ? t('Unlock {ref}', { ref: `V${dv.number}` }) : t('Lock V{n}', { n: dv.number })}
                        onClick={() => patchVehicle(dv, { locked: !dv.locked })}>
                        <LockIcon on={!!dv.locked} />
                      </button>
                    </div>
                    <div className="plan-col-pick">
                      <select className={`select ${dv.vehicleId ? '' : 'field-empty'}`} aria-label={t('Van of V{n}', { n: dv.number })} value={dv.vehicleId ?? ''}
                        onChange={(e) => patchVehicle(dv, { vehicleId: e.target.value || null })}>
                        <option value="">{t('Van…')}</option>
                        {(lists?.vehicles ?? []).filter((v) => v.active || v.id === dv.vehicleId).map((v: RefItem) => (
                          <option key={v.id} value={v.id} disabled={usedVehicles.has(v.id) && v.id !== dv.vehicleId}>
                            {str(v.plate)}
                          </option>
                        ))}
                      </select>
                      <select className={`select ${dv.guideId ? '' : 'field-empty'}`} aria-label={t(isT ? 'Driver of V{n}' : 'Guide of V{n}', { n: dv.number })} value={dv.guideId ?? ''}
                        onChange={(e) => patchVehicle(dv, { guideId: e.target.value || null })}>
                        <option value="">{t(isT ? 'Driver…' : 'Guide…')}</option>
                        {(lists?.guides ?? []).filter((g) => g.active || g.id === dv.guideId).map((g) => (
                          <option key={g.id} value={g.id} disabled={usedGuides.has(g.id) && g.id !== dv.guideId}>
                            {str(g.name)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {(dv.pmGuideId || pmOpen.has(dv.id)) ? (
                      <div className="plan-col-meta">
                        <select className="select" aria-label={t('Afternoon guide of V{n}', { n: dv.number })} value={dv.pmGuideId ?? ''}
                          onChange={(e) => {
                            const id = e.target.value || null;
                            if (!id) setPmOpen((o) => { const n = new Set(o); n.delete(dv.id); return n; });
                            void patchVehicle(dv, { pmGuideId: id === dv.guideId ? null : id });
                          }}>
                          <option value="">{t('Same guide all day')}</option>
                          {(lists?.guides ?? []).filter((g) => (g.active || g.id === dv.pmGuideId) && g.id !== dv.guideId).map((g) => (
                            <option key={g.id} value={g.id}>{str(g.name)}</option>
                          ))}
                        </select>
                        <span className="muted plan-pm-label">{t('afternoon')}</span>
                      </div>
                    ) : dv.guideId && !isT ? (
                      <button type="button" className="btn btn-ghost btn-sm plan-pm-add" onClick={() => setPmOpen((o) => new Set(o).add(dv.id))}>
                        {t('Other guide in the afternoon')}
                      </button>
                    ) : null}
                    <div className="plan-load">
                      {isT && <span>{trips.length === 1 ? t('1 trip') : t('{n} trips', { n: trips.length })}</span>}
                      {!isT && (['am', 'pm', 'eve'] as Segment[]).filter((s) => s !== 'eve' || load.eve).map((s) => (
                        <span key={s} className={load[s] > seats ? 'over' : load[s] === seats ? 'full' : ''}>
                          {t(SEGMENT_LABELS[s])} <b>{load[s]}/{seats}</b>
                        </span>
                      ))}
                      <label className="plan-seats plan-seats-inline" title={t('Seats for this date only')}>
                        <SeatsInput value={dv.seats} fallback={vehicle?.seats ?? 8} label={t('Seats of V{n} today', { n: dv.number })}
                          onSave={(v) => void patchVehicle(dv, { seats: v })} />
                        <span className="muted">{t('seats')}</span>
                      </label>
                    </div>
                    {inside.length === 0 && <div className="muted plan-empty">{phone ? t('Tap a booking to move it here.') : t('Drop bookings here.')}</div>}
                    {inside.length > 0 && <div className="plan-col-body">
                    {isT && trips.map((trip, ti) => {
                      const pax = trip.bookings.reduce((a, x) => a + x.pax, 0);
                      return (
                        <div key={trip.time ?? 'none'} className="plan-group">
                          <div className="plan-group-head">
                            <b className="mono">{trip.time ?? t('no time')}</b>
                            <span className={pax > seats ? 'warn-text' : 'muted'}>{t('{pax} of {seats} seats', { pax, seats })}</span>
                          </div>
                          {(() => {
                            // The first trip is driven by the vehicle's driver (chosen at the top of the card); a later trip
                            // can get another one (Bruno, 19/10/2026). Thin fields, as at the top of the card.
                            const row = day?.tripDrivers?.find((x) => x.dayVehicleId === dv.id && x.time === (trip.time ?? ''));
                            const driver = driverOf(dv, trip.time, day?.tripDrivers);
                            const own = !!row?.guideId && row.guideId !== dv.guideId;
                            const showDriver = ti > 0 || own;
                            const mainName = str(lists?.guides.find((g) => g.id === dv.guideId)?.name ?? '');
                            const saved = row?.notes ?? '';
                            return (
                              <div className={`plan-trip-fields ${showDriver ? 'two' : ''}`}>
                                {showDriver && (
                                  <select className={`select plan-trip-driver ${own ? 'own' : ''} ${driver ? '' : 'field-empty'}`}
                                    aria-label={t('Driver of the {time} trip of V{n}', { time: trip.time ?? t('no time'), n: dv.number })}
                                    value={own ? row!.guideId! : ''} disabled={!!dv.locked}
                                    onChange={(e) => void setTrip(dv, trip.time, { guideId: e.target.value || null })}>
                                    <option value="">{dv.guideId ? mainName : t('Driver…')}</option>
                                    {(lists?.guides ?? []).filter((g) => (g.active || g.id === driver) && g.id !== dv.guideId).map((g) => (
                                      <option key={g.id} value={g.id}>{str(g.name)}</option>
                                    ))}
                                  </select>
                                )}
                                <input key={`${dv.id}|${trip.time ?? ''}|${saved}`} className="input plan-trip-notes" defaultValue={saved} maxLength={500}
                                  placeholder={t('Observations (BDR)…')} disabled={!!dv.locked}
                                  aria-label={t('Observations of the {time} trip of V{n}', { time: trip.time ?? t('no time'), n: dv.number })}
                                  onBlur={(e) => void setTrip(dv, trip.time, { notes: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                              </div>
                            );
                          })()}
                          {trip.bookings.map((x) => {
                            const b = inside.find((y) => y.id === x.id)!;
                            return (
                              <BookingCard key={b.id} b={b} tour={tourOf(b)} brandColor={brandColor(b)} flash={flash[b.id]}
                                issue={bookingIssue(b.id)} onOpen={() => (phone ? setMoving(b) : setOpen(b))} frozen={b.planLocked || !!dv.locked}
                                onLock={() => toggleLock(b)} onStatus={(st) => void setStatus(b, st)} />
                            );
                          })}
                        </div>
                      );
                    })}
                    {!isT && sections.filter(([k]) => bySeg[k]?.length).map(([k, label]) => (
                      <div key={k} className="plan-group">
                        <div className="plan-group-head"><span className="muted">{t(label)}</span></div>
                        {bySeg[k]!.map((b) => (
                          <BookingCard key={b.id} b={b} tour={tourOf(b)} brandColor={brandColor(b)} tourColor={colorOf(b.tourId)} flash={flash[b.id]}
                            issue={bookingIssue(b.id)} onOpen={() => (phone ? setMoving(b) : setOpen(b))} frozen={b.planLocked || !!dv.locked}
                            onLock={() => toggleLock(b)} onStatus={(s) => void setStatus(b, s)} />
                        ))}
                      </div>
                    ))}
                    </div>}
                    {inside.length > 0 && (
                      <div className="plan-docs">
                        <button type="button" className={`btn btn-sm plan-bdr ${bdr[dv.id]?.state ?? ''}`} onClick={() => openBdr(dv)}
                          title={bdr[dv.id]?.at ? t('Last made {when}', { when: new Date(bdr[dv.id]!.at!).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) }) : t('Not made yet')}>
                          {bdr[dv.id]?.state === 'outdated' ? t('BDR out of date') : bdr[dv.id]?.state === 'current' ? 'BDR ✓' : 'BDR'}
                        </button>
                        {isT && (
                          <button type="button" className="btn btn-sm plan-welcome" onClick={() => openBdr(dv, 'signs')} title={t('One A4 sign per arriving client, with the logo of the booking’s brand')}>WELCOME</button>
                        )}
                      </div>
                    )}
                    {inside.length === 0 && !dv.locked && (
                      <button className="btn btn-ghost btn-sm btn-danger" onClick={() => removeVehicle(dv)}>{t('Remove V{n}', { n: dv.number })}</button>
                    )}
                  </Drop>
                );
              })}
            </div>
          </div>
          <DragOverlay>
            {dragging && (
              <div className={`pcard overlay ${colorOf(dragging.tourId) ? 'tinted' : ''}`} style={{ ...tourTint(colorOf(dragging.tourId)), borderLeftColor: brandColor(dragging) }}>
                <CardBody b={dragging} tour={tourOf(dragging)} />
              </div>
            )}
          </DragOverlay>
        </DndContext>
        </>
      )}

      {allAlerts && day && (
        <div className="alerts-backdrop" onClick={() => setAllAlerts(false)} onKeyDown={(e) => e.key === 'Escape' && setAllAlerts(false)}>
          <div className="alerts-modal card" role="dialog" aria-modal="true" aria-label={t('Alerts of the day')} onClick={(e) => e.stopPropagation()}>
            <div className="sheet-head"><h2>{t('Alerts of the day')}</h2><button className="btn btn-ghost btn-sm" autoFocus onClick={() => setAllAlerts(false)}>{t('Close')}</button></div>
            <div className="sheet-body">
              {(() => {
                const groups: { label: string; list: PlanIssue[] }[] = [];
                const general = alertsHere.filter((i) => !i.dayVehicleId && i.level !== 'info');
                if (general.length) groups.push({ label: t('Day'), list: general });
                for (const dv of vehiclesHere) {
                  const list = alerts.filter((i) => i.dayVehicleId === dv.id && i.level !== 'info' && !SHOWN_IN_FIELDS.has(i.code));
                  if (list.length) groups.push({ label: `V${dv.number}`, list });
                }
                return groups.map((g) => (
                  <div key={g.label} className="plan-group">
                    <div className="plan-group-head"><b>{g.label}</b><span className="muted">{g.list.length}</span></div>
                    <ul className="plan-issues">
                      {[...g.list].sort((a, b) => (a.level === 'block' ? 0 : 1) - (b.level === 'block' ? 0 : 1)).map((i, k) => <li key={k} className={i.level}>{i.message}</li>)}
                    </ul>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
      {tourSheet && rl?.tourMap.get(tourSheet) && (
        <TourDaySheet tour={rl.tourMap.get(tourSheet)!} date={date} current={day?.dayTours.find((d) => d.tourId === tourSheet)}
          pax={toursToday.find((x) => x.tour.id === tourSheet)?.pax ?? 0} onClose={() => setTourSheet(null)} onSaved={() => void load()} />
      )}

      {proposal && day && (
        <div className="sheet-backdrop" onClick={() => setProposal(null)}>
          <div className="sheet card" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('Auto-plan proposal')}>
            <div className="sheet-head"><h2>{t('Auto-plan proposal')}</h2><button className="btn btn-ghost btn-sm" onClick={() => setProposal(null)}>{t('Discard')}</button></div>
            <div className="sheet-body">
              <p className="muted">
                {t('Bookings to place: {n}. New vehicles: {v}.', { n: proposal.moves.length, v: proposal.newVehicles.length })}{' '}
                {t('Locked bookings and vehicles are not touched. Nothing is saved until you accept.')}
              </p>
              {[...new Set(proposal.moves.map((m) => m.to))].map((to) => {
                const isNew = to.startsWith(NEW_VEHICLE_PREFIX);
                const number = isNew ? proposal.newVehicles.find((v) => v.id === to)?.number : day.dayVehicles.find((d) => d.id === to)?.number;
                const list = proposal.moves.filter((m) => m.to === to).map((m) => day.bookings.find((b) => b.id === m.bookingId)!).filter(Boolean);
                return (
                  <div key={to} className="plan-group">
                    <div className="plan-group-head"><b>V{number}{isNew ? ` ${t('(new)')}` : ''}</b><span className="muted">{t('{n} pax added', { n: list.reduce((s, b) => s + b.pax, 0) })}</span></div>
                    {list.map((b) => (
                      <div key={b.id} className={`pcard ${colorOf(b.tourId) ? 'tinted' : ''}`} style={{ ...tourTint(colorOf(b.tourId)), borderLeftColor: brandColor(b) }}><CardBody b={b} tour={tourOf(b)} /></div>
                    ))}
                  </div>
                );
              })}
              {proposal.unplaced.length > 0 && (
                <div className="plan-group">
                  <div className="plan-group-head"><b>{t('Not placed')}</b></div>
                  <ul className="plan-issues">
                    {proposal.unplaced.map((u) => {
                      const b = day.bookings.find((x) => x.id === u.bookingId);
                      return <li key={u.bookingId}>{b?.reference}: {u.reason}</li>;
                    })}
                  </ul>
                </div>
              )}
            </div>
            <div className="sheet-foot">
              <button className="btn btn-primary" onClick={applyAuto} disabled={working || !proposal.moves.length}>{t('Accept')}</button>
              <button className="btn" onClick={() => setProposal(null)}>{t('Discard')}</button>
            </div>
          </div>
        </div>
      )}

      {moving && day && rl && (() => {
        const b = day.bookings.find((x) => x.id === moving.id) ?? moving;
        const here = b.dayVehicleId;
        const hereDv = day.dayVehicles.find((d) => d.id === here);
        const frozen = b.planLocked || !!hereDv?.locked;
        // Same checks as a drop, shown before the tap.
        const why = (to: string | null): string => {
          const locked = lockReason(toInput(day, rl), b.id, to, t);
          if (locked) return locked;
          const next = { ...day, bookings: day.bookings.map((x) => (x.id === b.id ? { ...x, dayVehicleId: to } : x)) };
          return newBlocks(checkDay(toInput(day, rl), t), checkDay(toInput(next, rl), t)).map((i) => i.message).join(' ');
        };
        const go = (to: string | null) => { setMoving(null); void move(b, to); };
        return (
          <div className="sheet-backdrop" onClick={() => setMoving(null)}>
            <div className="sheet card move-sheet" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={t('Move {ref}', { ref: b.reference })}>
              <div className="sheet-head">
                <div style={{ minWidth: 0, marginRight: 'auto' }}>
                  <h2 className="ellipsis">{displayName(b) || b.reference}</h2>
                  {b.clientNameLatin && <div className="muted ellipsis" style={{ fontSize: 13 }}>{b.clientName}</div>}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setMoving(null)}>{t('Close')}</button>
              </div>
              <div className="sheet-body">
                <div className={`pcard ${colorOf(b.tourId) ? 'tinted' : ''}`} style={{ ...tourTint(colorOf(b.tourId)), borderLeftColor: brandColor(b) }}><CardBody b={b} tour={tourOf(b)} /></div>
                <button className="btn btn-block" onClick={() => { setMoving(null); setOpen(b); }}>{t('Open booking')}</button>
                {frozen ? (
                  <div className="alert alert-warn">
                    {b.planLocked ? t('This booking is locked in its vehicle.') : t('V{n} is locked: nothing goes in or out.', { n: hereDv?.number ?? '' })}
                    {b.planLocked && !hereDv?.locked && (
                      <div><button className="btn btn-sm" style={{ marginTop: 8 }} onClick={() => { setMoving(null); void toggleLock(b); }}>{t('Unlock')}</button></div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="plan-group-head"><span className="muted">{t('Move to')}</span></div>
                    <div className="move-list">
                      {here && (() => {
                        const r = why(null);
                        return (
                          <button type="button" className="move-opt" disabled={!!r || !online} onClick={() => go(null)}>
                            <b>{t('To place')}</b><span className="muted">{r || t('Take it out of the vehicle')}</span>
                          </button>
                        );
                      })()}
                      <button type="button" className="move-opt move-new" disabled={!online || working}
                        onClick={() => { setMoving(null); void moveToNew(b); }}>
                        <b>{t('New vehicle (V{n})', { n: Math.max(0, ...day.dayVehicles.map((d) => d.number)) + 1 })}</b>
                        <span className="muted">{t('Open a new V with this booking in it')}</span>
                      </button>
                      {vehiclesHere.map((dv) => {
                        if (dv.id === here) return null;
                        const inside = live.filter((x) => x.dayVehicleId === dv.id);
                        const seats = seatsOf(dv, rl.vehicles);
                        const ld = loadOf(inside.map((x) => ({ ...x, missing: false })), rl.tourMap);
                        const plate = str(rl.vehicles.find((v) => v.id === dv.vehicleId)?.plate) || t('No van');
                        const guide = str(lists?.guides.find((g) => g.id === dv.guideId)?.name) || t('No guide');
                        const codes = [...new Set(inside.map((x) => tourOf(x)?.code).filter(Boolean))].join(', ');
                        const r = why(dv.id);
                        return (
                          <button key={dv.id} type="button" className="move-opt" disabled={!!r || !online} onClick={() => go(dv.id)}>
                            <b>V{dv.number}</b>
                            <span>{codes || t('empty')} · {t('AM')} {ld.am}/{seats} · {t('PM')} {ld.pm}/{seats}</span>
                            <span className="muted">{r || `${plate} · ${guide}`}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {open && (
        <BookingPanel key={open.id} booking={open} date={date} onClose={() => setOpenRaw(null)} onSaved={() => void load()} />
      )}
    </div>
  );
}
